/**
 * ============================================================
 *  Tina Jewellery — Dynamic Currency Converter
 *  Works with Shopify's country switcher (no Shopify Payments
 *  needed). Detects selected country → fetches live FX rate
 *  vs EGP → rewrites all prices on the page in real time.
 *
 *  INSTALL:
 *    Paste this script in Shopify Admin →
 *    Online Store → Themes → Edit code →
 *    layout/theme.liquid  just before </body>
 *
 *    <script src="{{ 'tina-currency-switcher.js' | asset_url }}" defer></script>
 *
 *  OR paste the whole thing inside a <script> tag in theme.liquid.
 * ============================================================
 */

(function () {
  'use strict';

  // ── CONFIG ────────────────────────────────────────────────
  const BASE_CURRENCY = 'EGP';   // Your Shopify store's base currency

  /**
   * Map Shopify country codes → ISO 4217 currency codes.
   * Extend this list for any country you sell to.
   */
  const COUNTRY_CURRENCY_MAP = {
    EG: 'EGP',  // Egypt (base)
    US: 'USD',  // United States
    GB: 'GBP',  // United Kingdom
    DE: 'EUR',  // Germany
    FR: 'EUR',  // France
    IT: 'EUR',  // Italy
    ES: 'EUR',  // Spain
    NL: 'EUR',  // Netherlands
    BE: 'EUR',  // Belgium
    AE: 'AED',  // UAE
    SA: 'SAR',  // Saudi Arabia
    KW: 'KWD',  // Kuwait
    QA: 'QAR',  // Qatar
    BH: 'BHD',  // Bahrain
    OM: 'OMR',  // Oman
    JO: 'JOD',  // Jordan
    LB: 'LBP',  // Lebanon
    CA: 'CAD',  // Canada
    AU: 'AUD',  // Australia
    CH: 'CHF',  // Switzerland
    SE: 'SEK',  // Sweden
    NO: 'NOK',  // Norway
    DK: 'DKK',  // Denmark
    JP: 'JPY',  // Japan
    CN: 'CNY',  // China
    IN: 'INR',  // India
    TR: 'TRY',  // Turkey
    MA: 'MAD',  // Morocco
    NG: 'NGN',  // Nigeria
    ZA: 'ZAR',  // South Africa
  };

  /**
   * Currency symbol / formatting overrides.
   * If not listed here, the browser's Intl.NumberFormat will
   * pick a sensible default.
   */
  const CURRENCY_FORMAT = {
    EGP: { symbol: 'EGP ', decimals: 2 },
    USD: { symbol: '$',    decimals: 2 },
    GBP: { symbol: '£',    decimals: 2 },
    EUR: { symbol: '€',    decimals: 2 },
    AED: { symbol: 'AED ', decimals: 2 },
    SAR: { symbol: 'SAR ', decimals: 2 },
    KWD: { symbol: 'KD ',  decimals: 3 },
    QAR: { symbol: 'QR ',  decimals: 2 },
    BHD: { symbol: 'BD ',  decimals: 3 },
    OMR: { symbol: 'OMR ', decimals: 3 },
    JOD: { symbol: 'JD ',  decimals: 3 },
    CAD: { symbol: 'CA$',  decimals: 2 },
    AUD: { symbol: 'A$',   decimals: 2 },
    CHF: { symbol: 'CHF ', decimals: 2 },
    JPY: { symbol: '¥',    decimals: 0 },
    INR: { symbol: '₹',    decimals: 2 },
    TRY: { symbol: '₺',    decimals: 2 },
    MAD: { symbol: 'MAD ', decimals: 2 },
    NGN: { symbol: '₦',    decimals: 2 },
    ZAR: { symbol: 'R ',   decimals: 2 },
  };

  // ── STATE ─────────────────────────────────────────────────
  let activeCountry   = BASE_CURRENCY;   // currently selected country code
  let activeCurrency  = BASE_CURRENCY;   // currently active currency code
  let activeRate      = 1;               // conversion rate vs EGP
  let rateCache       = {};              // { 'USD': 0.0204, 'AED': 0.075, … }
  let originalPrices  = new Map();       // el → original EGP value in cents

  // ── RATE FETCHING ─────────────────────────────────────────
  /**
   * Uses the free ExchangeRate-API open endpoint (no key needed,
   * ~1500 req/month per IP — more than enough for a storefront).
   * Falls back to frankfurter.app if the first fails.
   */
  async function fetchRates() {
    // Try primary: exchangerate-api free tier
    try {
      const res = await fetch(
        `https://open.er-api.com/v6/latest/${BASE_CURRENCY}`
      );
      if (res.ok) {
        const data = await res.json();
        if (data.result === 'success') {
          rateCache = data.rates;          // rates[USD] = how many USD per 1 EGP
          return;
        }
      }
    } catch (_) {}

    // Fallback: frankfurter.app (ECB data, free, no key)
    try {
      const res = await fetch(
        `https://api.frankfurter.app/latest?from=${BASE_CURRENCY}`
      );
      if (res.ok) {
        const data = await res.json();
        rateCache = { [BASE_CURRENCY]: 1, ...data.rates };
        return;
      }
    } catch (_) {}

    console.warn('[TinaCurrency] Could not fetch exchange rates. Prices unchanged.');
  }

  // ── COUNTRY DETECTION ─────────────────────────────────────
  /**
   * Reads the currently selected country from Shopify's
   * localization form.  Shopify renders something like:
   *
   *   <form action="/localization" method="post">
   *     <select name="country_code">
   *       <option value="EG" selected>Egypt</option>
   *       …
   *     </select>
   *   </form>
   *
   * OR uses a disclosure/button pattern in newer themes.
   * We handle both.
   */
  function getSelectedCountry() {
    // 1. Standard <select name="country_code">
    const sel = document.querySelector(
      'select[name="country_code"], select[name="country"]'
    );
    if (sel && sel.value) return sel.value.toUpperCase();

    // 2. Hidden input that some themes use
    const hidden = document.querySelector(
      'input[name="country_code"], input[name="country"]'
    );
    if (hidden && hidden.value) return hidden.value.toUpperCase();

    // 3. Shopify's Localization form data attribute (Dawn theme)
    const form = document.querySelector('localization-form, [data-country]');
    if (form) {
      const val = form.dataset.country || form.getAttribute('data-country');
      if (val) return val.toUpperCase();
    }

    // 4. URL param ?country=XX  (set by some theme switches)
    const urlParam = new URLSearchParams(window.location.search).get('country');
    if (urlParam) return urlParam.toUpperCase();

    // 5. Shopify cookie _shopify_country
    const cookie = document.cookie.split('; ').find(r => r.startsWith('_shopify_country='));
    if (cookie) return cookie.split('=')[1].toUpperCase();

    // 6. Shopify global object (set in some themes)
    if (window.Shopify && window.Shopify.country)
      return window.Shopify.country.toUpperCase();

    return 'EG'; // default to Egypt
  }

  // ── PRICE ELEMENT DISCOVERY ───────────────────────────────
  /**
   * Returns all .money elements — this store uses Shopify's
   * standard money format class exclusively.
   */
  function getPriceElements() {
    return document.querySelectorAll('.money');
  }

  // ── PRICE PARSING ─────────────────────────────────────────
  /**
   * Extracts a numeric EGP value from a .money element.
   *
   * Handles both Shopify money formats used on this store:
   *   "LE 1,250.00 EGP"   (with_currency format)
   *   "LE 1,250.00"       (without_currency format)
   *
   * The original raw HTML text is cached so we can restore it
   * exactly and re-parse accurately on future currency switches.
   */
  function parseEGP(el) {
    if (originalPrices.has(el)) return originalPrices.get(el);

    const raw = el.textContent.trim();

    // Remove "LE" prefix and optional " EGP" suffix, then strip commas
    // Matches: "LE 1,250.00 EGP" or "LE 1,250.00"
    const cleaned = raw
      .replace(/^LE\s*/i, '')      // strip leading "LE "
      .replace(/\s*EGP\s*$/i, '')  // strip trailing " EGP"
      .replace(/,/g, '')           // remove thousands commas → "1250.00"
      .trim();

    const value = parseFloat(cleaned);
    if (isNaN(value)) return null;

    // Cache the original raw text so we can restore it perfectly
    el.dataset.tinaOriginal = raw;
    originalPrices.set(el, value);
    return value;
  }

  // ── PRICE FORMATTING ──────────────────────────────────────
  /**
   * Converts an EGP amount to the target currency string.
   * Output format mirrors the store's own money format:
   *   Base (EGP):       "LE 1,250.00 EGP"  or  "LE 1,250.00"
   *   Other currency:   "$ 96.25"  /  "AED 353.80"  etc.
   */
  function formatPrice(valueInEGP, currency, rate) {
    const converted = valueInEGP * rate;
    const fmt       = CURRENCY_FORMAT[currency];
    const decimals  = fmt ? fmt.decimals : 2;
    const symbol    = fmt ? fmt.symbol   : currency + ' ';

    const fixed  = converted.toFixed(decimals);
    const parts  = fixed.split('.');
    parts[0]     = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount = parts.join('.');

    return symbol + amount;
  }

  // ── REWRITE PRICES ────────────────────────────────────────
  function rewritePrices() {
    const currency = activeCurrency;
    const rate     = activeRate;

    if (currency === BASE_CURRENCY) {
      // Restore every element to its original store text exactly
      getPriceElements().forEach(el => {
        if (el.dataset.tinaOriginal) {
          el.textContent = el.dataset.tinaOriginal;
        }
      });
      return;
    }

    getPriceElements().forEach(el => {
      const egpValue = parseEGP(el);
      if (egpValue === null) return;
      el.textContent = formatPrice(egpValue, currency, rate);
    });
  }

  // ── COUNTRY PICKER POPUP ──────────────────────────────────
  /**
   * Full-screen unskippable modal shown on first visit (per session).
   * Stores choice in sessionStorage so it only fires once per tab session.
   * On pick → sets activeCurrency/activeRate → rewrites prices → syncs
   * Shopify's own header selector so the two stay in agreement.
   */

  const POPUP_COUNTRIES = [
    { code: 'EG', flag: '🇪🇬', name: 'Egypt',         sub: 'EGP — Egyptian Pound' },
    { code: 'US', flag: '🇺🇸', name: 'United States',  sub: 'USD — US Dollar' },
    { code: 'GB', flag: '🇬🇧', name: 'United Kingdom', sub: 'GBP — Pound Sterling' },
    { code: 'AE', flag: '🇦🇪', name: 'UAE',            sub: 'AED — Dirham' },
    { code: 'SA', flag: '🇸🇦', name: 'Saudi Arabia',   sub: 'SAR — Riyal' },
    { code: 'KW', flag: '🇰🇼', name: 'Kuwait',         sub: 'KWD — Dinar' },
    { code: 'QA', flag: '🇶🇦', name: 'Qatar',          sub: 'QAR — Riyal' },
    { code: 'BH', flag: '🇧🇭', name: 'Bahrain',        sub: 'BHD — Dinar' },
    { code: 'OM', flag: '🇴🇲', name: 'Oman',           sub: 'OMR — Rial' },
    { code: 'JO', flag: '🇯🇴', name: 'Jordan',         sub: 'JOD — Dinar' },
    { code: 'DE', flag: '🇩🇪', name: 'Germany',        sub: 'EUR — Euro' },
    { code: 'FR', flag: '🇫🇷', name: 'France',         sub: 'EUR — Euro' },
    { code: 'IT', flag: '🇮🇹', name: 'Italy',          sub: 'EUR — Euro' },
    { code: 'CA', flag: '🇨🇦', name: 'Canada',         sub: 'CAD — Dollar' },
    { code: 'AU', flag: '🇦🇺', name: 'Australia',      sub: 'AUD — Dollar' },
    { code: 'CH', flag: '🇨🇭', name: 'Switzerland',    sub: 'CHF — Franc' },
    { code: 'MA', flag: '🇲🇦', name: 'Morocco',        sub: 'MAD — Dirham' },
    { code: 'TR', flag: '🇹🇷', name: 'Turkey',         sub: 'TRY — Lira' },
    { code: 'IN', flag: '🇮🇳', name: 'India',          sub: 'INR — Rupee' },
    { code: 'NG', flag: '🇳🇬', name: 'Nigeria',        sub: 'NGN — Naira' },
  ];

  const POPUP_SESSION_KEY    = 'tina_country_chosen';   // set = popup won't show
  const POPUP_COUNTRY_KEY    = 'tina_chosen_country';   // stores the ISO code e.g. "AE"

  function hasChosenCountry() {
    try { return !!sessionStorage.getItem(POPUP_SESSION_KEY); } catch (_) { return false; }
  }

  function saveChosenCountry(code) {
    try {
      sessionStorage.setItem(POPUP_SESSION_KEY, '1');
      sessionStorage.setItem(POPUP_COUNTRY_KEY, code);
    } catch (_) {}
  }

  function getStoredCountry() {
    try { return sessionStorage.getItem(POPUP_COUNTRY_KEY) || null; } catch (_) { return null; }
  }

  /**
   * Submit Shopify's native localization form programmatically.
   *
   * Shopify's theme renders a <form action="/" method="post"> (or action="/localization")
   * containing hidden inputs including `_method`, `authenticity_token`, and
   * `country_code`. We find that form, update the country_code input, and submit.
   * This triggers a full page reload with the correct Shopify market applied,
   * so prices come back from Shopify already set for the right market.
   *
   * After the reload, sessionStorage still has our chosen country, so we skip
   * the popup and apply the FX conversion on top of the now-correct market prices.
   */
  function submitShopifyLocalizationForm(countryCode) {
    // Find Shopify's localization form (present in virtually all themes)
    const form = document.querySelector(
      'form[action="/localization"], form[action="/"], form[action=""]'
    );

    if (form) {
      // Update or create the country_code input
      let input = form.querySelector('input[name="country_code"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'country_code';
        form.appendChild(input);
      }
      input.value = countryCode;

      // Some themes use a <select> for country_code — update that too
      const sel = form.querySelector('select[name="country_code"]');
      if (sel) sel.value = countryCode;

      form.submit();
      return true;
    }

    // Fallback: if no form found, do a redirect with ?country= param
    // (Shopify respects this on many themes)
    const url = new URL(window.location.href);
    url.searchParams.set('country', countryCode);
    window.location.href = url.toString();
    return false;
  }

  function onCountryPicked(countryCode, overlayEl) {
    // 1. Save to sessionStorage BEFORE the reload wipes JS state
    saveChosenCountry(countryCode);

    // 2. Show a loading state on the modal instead of removing it
    //    (page is about to reload anyway, this prevents flash)
    const modal = overlayEl.querySelector('#tina-country-modal');
    if (modal) {
      modal.innerHTML = `
        <div style="padding:60px 40px;text-align:center;">
          <div style="font-size:32px;margin-bottom:16px;">🌍</div>
          <p style="margin:0;font-size:15px;font-weight:600;color:#111;">Setting your region…</p>
          <p style="margin:8px 0 0;font-size:13px;color:#999;">Just a moment</p>
        </div>
      `;
    }

    // 3. Submit Shopify's localization form → page reload with correct market
    //    Small delay so the loading UI renders first
    setTimeout(() => submitShopifyLocalizationForm(countryCode), 120);
  }

  function showCountryPickerPopup() {
    if (hasChosenCountry()) return;
    const currentCountry = getStoredCountry() || getSelectedCountry();

    // Skip popup for Egyptian users — covers three cases:
    //  1. Country explicitly detected/stored as EG
    //  2. Any country that maps to EGP (e.g. unknown → falls back to base)
    //  3. Shopify's Egypt market is active (no foreign-country signals present)
    const isEgyptMarket =
      currentCountry === 'EG' ||
      (COUNTRY_CURRENCY_MAP[currentCountry] || BASE_CURRENCY) === BASE_CURRENCY;

    if (isEgyptMarket) {
      saveChosenCountry(currentCountry || 'EG');
      return;
    }
    // Styles
    const style = document.createElement('style');
    style.textContent = `
      #tina-country-overlay {
        position: fixed; inset: 0; z-index: 999999;
        background: rgba(10,10,10,.72);
        backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
        display: flex; align-items: center; justify-content: center;
        padding: 16px; opacity: 0;
        transition: opacity .35s ease;
        font-family: inherit;
      }
      #tina-country-overlay.tina-visible { opacity: 1; }
      #tina-country-modal {
        background: #fff; border-radius: 16px;
        width: 100%; max-width: 520px; max-height: 90vh;
        overflow: hidden; display: flex; flex-direction: column;
        box-shadow: 0 32px 80px rgba(0,0,0,.35);
        transform: translateY(28px);
        transition: transform .4s cubic-bezier(.22,1,.36,1);
      }
      #tina-country-overlay.tina-visible #tina-country-modal { transform: translateY(0); }
      #tina-cp-header { padding: 28px 28px 0; text-align: center; }
      #tina-cp-header .tina-eyebrow {
        font-size: 10.5px; letter-spacing: .2em; text-transform: uppercase;
        color: #aaa; margin-bottom: 12px;
      }
      #tina-cp-header h2 {
        margin: 0 0 8px; font-size: 21px; font-weight: 700; color: #111; line-height: 1.3;
      }
      #tina-cp-header p { margin: 0 0 20px; font-size: 13px; color: #777; line-height: 1.55; }
      #tina-cp-search-wrap { padding: 0 16px 10px; position: relative; }
      #tina-cp-search {
        width: 100%; box-sizing: border-box;
        padding: 10px 14px 10px 36px;
        border: 1.5px solid #e0e0e0; border-radius: 8px;
        font-size: 13.5px; outline: none; color: #111; background: #fafafa;
        transition: border-color .2s, background .2s;
      }
      #tina-cp-search:focus { border-color: #111; background: #fff; }
      #tina-cp-search-wrap::before {
        content: ''; position: absolute; left: 28px; top: 50%;
        transform: translateY(-50%);
        width: 14px; height: 14px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23aaa' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E");
        background-repeat: no-repeat; background-size: contain; pointer-events: none;
      }
      #tina-cp-list { overflow-y: auto; padding: 4px 10px 20px; flex: 1; }
      #tina-cp-list::-webkit-scrollbar { width: 4px; }
      #tina-cp-list::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 4px; }
      .tina-cp-btn {
        width: 100%; display: flex; align-items: center; gap: 12px;
        padding: 10px 12px; margin-bottom: 3px;
        background: none; border: 1.5px solid transparent;
        border-radius: 10px; cursor: pointer; text-align: left;
        transition: background .14s, border-color .14s;
      }
      .tina-cp-btn:hover { background: #f6f6f6; border-color: #e8e8e8; }
      .tina-cp-btn:active { background: #efefef; }
      .tina-cp-btn .tina-flag { font-size: 22px; line-height: 1; flex-shrink: 0; }
      .tina-cp-btn .tina-info { flex: 1; min-width: 0; }
      .tina-cp-btn .tina-info strong { display: block; font-size: 13.5px; font-weight: 600; color: #111; }
      .tina-cp-btn .tina-info span  { font-size: 11.5px; color: #999; }
      .tina-cp-btn .tina-arrow { font-size: 14px; color: #ccc; flex-shrink: 0; }
      .tina-cp-btn:hover .tina-arrow { color: #888; }
      .tina-cp-noresults { text-align: center; padding: 24px 0; color: #bbb; font-size: 13px; }
    `;
    document.head.appendChild(style);

    const buttons = POPUP_COUNTRIES.map(c => `
      <button class="tina-cp-btn" data-code="${c.code}" type="button">
        <span class="tina-flag">${c.flag}</span>
        <span class="tina-info">
          <strong>${c.name}</strong>
          <span>${c.sub}</span>
        </span>
        <span class="tina-arrow">›</span>
      </button>
    `).join('');

    const overlay = document.createElement('div');
    overlay.id = 'tina-country-overlay';
    overlay.innerHTML = `
      <div id="tina-country-modal" role="dialog" aria-modal="true" aria-labelledby="tina-cp-title">
        <div id="tina-cp-header">
          <div class="tina-eyebrow">Tina Jewellery</div>
          <h2 id="tina-cp-title">Where are you shopping from?</h2>
          <p>Select your country so we can display prices<br>in your local currency.</p>
        </div>
        <div id="tina-cp-search-wrap">
          <input id="tina-cp-search" type="text"
            placeholder="Search country…" autocomplete="off" spellcheck="false" />
        </div>
        <div id="tina-cp-list">${buttons}</div>
      </div>
    `;
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    // Animate in
    requestAnimationFrame(() => requestAnimationFrame(() =>
      overlay.classList.add('tina-visible')
    ));

    // Search filter
    overlay.querySelector('#tina-cp-search').addEventListener('input', function () {
      const q = this.value.trim().toLowerCase();
      const list = overlay.querySelector('#tina-cp-list');
      let any = false;
      overlay.querySelectorAll('.tina-cp-btn').forEach(btn => {
        const show = !q || btn.textContent.toLowerCase().includes(q);
        btn.style.display = show ? '' : 'none';
        if (show) any = true;
      });
      let noRes = list.querySelector('.tina-cp-noresults');
      if (!any && !noRes) {
        noRes = document.createElement('p');
        noRes.className = 'tina-cp-noresults';
        noRes.textContent = 'No countries found.';
        list.appendChild(noRes);
      } else if (any && noRes) {
        noRes.remove();
      }
    });

    // Pick handler
    overlay.querySelectorAll('.tina-cp-btn').forEach(btn => {
      btn.addEventListener('click', () => onCountryPicked(btn.dataset.code, overlay));
    });

    // Focus search after animation
    setTimeout(() => overlay.querySelector('#tina-cp-search').focus(), 420);
  }

  // ── BADGE INDICATOR ───────────────────────────────────────
  /**
   * Shows a small floating badge so customers know which
   * currency they're viewing. Dismiss-able.
   */
  function showCurrencyBadge(currency) {
    const existing = document.getElementById('tina-currency-badge');
    if (existing) existing.remove();
    if (currency === BASE_CURRENCY) return;

    const badge = document.createElement('div');
    badge.id = 'tina-currency-badge';
    badge.innerHTML = `
      <span>Prices shown in <strong>${currency}</strong></span>
      <small style="opacity:.6;display:block;font-size:11px;margin-top:2px;">
        Checkout will be in EGP
      </small>
      <button onclick="this.parentElement.remove()"
              style="position:absolute;top:6px;right:8px;background:none;
                     border:none;cursor:pointer;font-size:14px;opacity:.5">✕</button>
    `;
    Object.assign(badge.style, {
      position:     'fixed',
      bottom:       '20px',
      right:        '20px',
      background:   '#1a1a1a',
      color:        '#fff',
      padding:      '12px 32px 12px 16px',
      borderRadius: '8px',
      fontSize:     '13px',
      zIndex:       '99999',
      boxShadow:    '0 4px 20px rgba(0,0,0,.25)',
      lineHeight:   '1.4',
      fontFamily:   'inherit',
    });
    document.body.appendChild(badge);
  }

  // ── MAIN UPDATE FLOW ──────────────────────────────────────
  async function updateCurrency() {
    const country  = getSelectedCountry();
    const currency = COUNTRY_CURRENCY_MAP[country] || BASE_CURRENCY;

    if (currency === activeCurrency) return; // no change

    activeCurrency = currency;
    activeCountry  = country;

    if (currency === BASE_CURRENCY) {
      activeRate = 1;
      rewritePrices();
      showCurrencyBadge(currency);
      return;
    }

    // Fetch rates if not cached yet
    if (Object.keys(rateCache).length === 0) {
      await fetchRates();
    }

    const rate = rateCache[currency];
    if (!rate) {
      console.warn(`[TinaCurrency] No rate found for ${currency}`);
      return;
    }

    activeRate = rate;
    rewritePrices();
    showCurrencyBadge(currency);
  }

  // ── MUTATION OBSERVER ─────────────────────────────────────
  /**
   * Shopify themes load cart drawers, quick-buy panels, and
   * collection filters via AJAX. We watch the whole document
   * for new price elements and convert them immediately.
   */
  let mutationTimer = null;
  const observer = new MutationObserver(() => {
    if (activeCurrency === BASE_CURRENCY) return;
    clearTimeout(mutationTimer);
    mutationTimer = setTimeout(rewritePrices, 80);
  });

  // ── HEADER SELECTOR LISTENER ──────────────────────────────
  /**
   * If the customer uses the native header country selector (not our popup),
   * save their choice to sessionStorage before Shopify's page reload,
   * so the FX conversion is applied correctly on the next load.
   */
  function attachCountrySelectorListeners() {
    document.querySelectorAll(
      'select[name="country_code"], select[name="country"]'
    ).forEach(sel => {
      sel.addEventListener('change', () => {
        saveChosenCountry(sel.value.toUpperCase());
        // Let Shopify's own form submit handle the reload naturally
      });
    });

    // Dawn / Sense / Refresh themes use <localization-form>
    document.querySelectorAll('localization-form').forEach(form => {
      form.addEventListener('change', e => {
        const val = e.target.value;
        if (val && val.length === 2) saveChosenCountry(val.toUpperCase());
      });
    });
  }

  // ── SHOPIFY AJAX CART EVENT ───────────────────────────────
  // Shopify fires these on the document after cart updates
  document.addEventListener('cart:updated', rewritePrices);
  document.addEventListener('cart:refresh', rewritePrices);

  // ── BOOT ─────────────────────────────────────────────────
  async function boot() {
    // Pre-fetch rates in background so they're ready
    await fetchRates();

    /**
     * Country resolution priority:
     *  1. sessionStorage — set by our popup OR header selector listener.
     *     This is the authoritative source after a form-submit reload,
     *     because Shopify's page reload resets all JS state.
     *  2. Shopify's native header selector / localization object / URL param
     *     (fallback for direct deep-links or very first page load)
     */
    const storedCountry = getStoredCountry();
    const country       = storedCountry || getSelectedCountry();
    const currency      = COUNTRY_CURRENCY_MAP[country] || BASE_CURRENCY;

    activeCurrency = currency;
    activeCountry  = country;
    activeRate     = rateCache[currency] || 1;

    // After a Shopify localization reload, sync the header selector UI
    // to whatever country we stored (in case theme hasn't updated it yet)
    if (storedCountry) {
      document.querySelectorAll(
        'select[name="country_code"], select[name="country"]'
      ).forEach(sel => {
        if (sel.querySelector(`option[value="${storedCountry}"]`)) {
          sel.value = storedCountry;
        }
      });
    }

    // Show popup only if no country chosen yet this session
    showCountryPickerPopup();

    // Apply FX conversion (post-reload prices are already market-correct EGP)
    if (currency !== BASE_CURRENCY) {
      rewritePrices();
      showCurrencyBadge(currency);
    }

    // Attach listener so native header changes also persist to sessionStorage
    attachCountrySelectorListeners();

    // Watch DOM for dynamically loaded price elements (cart drawer, quick-buy…)
    observer.observe(document.body, { childList: true, subtree: true });

    // Refresh FX rates every 30 min for long sessions
    setInterval(async () => {
      rateCache = {};
      await fetchRates();
      activeRate = rateCache[activeCurrency] || 1;
      if (activeCurrency !== BASE_CURRENCY) rewritePrices();
    }, 30 * 60 * 1000);

    console.log(`[TinaCurrency] Country: ${country} (${storedCountry ? 'session' : 'detected'}) | Currency: ${currency} | Rate: ${activeRate}`);
  }

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();